import { AxiosError } from 'axios'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import IResultSet from '../../../api/types/resultSet'
import { IAlphaprocessState } from '../../../store'
import { alert } from '../../../util'
import DOMPurify from 'dompurify';

/**
 * Builds a clean, human-readable filename for checklist exports.
 * Format: {Title}_{Location}_{YYYY-MM-DD}.{ext}
 * - Replaces spaces with hyphens, strips percent-encoded or special characters
 * - Location segment is omitted if empty
 * - Date falls back to due_date, then today
 */
export function buildResultSetFilename(
  title: string,
  location: string | null | undefined,
  date: Date | string | null | undefined,
  ext: string
): string {
  const slugify = (s: string) =>
    s.trim()
      .replace(/[^\w\säöüÄÖÜß\-]/g, '') // strip special chars except letters, digits, umlauts, spaces, hyphens
      .replace(/\s+/g, '-')              // spaces → hyphens
      .replace(/-{2,}/g, '-')            // collapse consecutive hyphens
      .replace(/^-|-$/g, '')             // trim leading/trailing hyphens

  const titleSlug = slugify(title || 'export')

  const locationSlug = location ? slugify(location) : ''

  const dateStr = (() => {
    const d = date ? new Date(date as string) : new Date()
    if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 10)
    return d.toISOString().slice(0, 10)
  })()

  const parts = [titleSlug, locationSlug, dateStr].filter(Boolean)
  return `${parts.join('_')}.${ext}`
}

interface IProps {
  children: any
  resultSet: IResultSet
  onFailure?: (error: Error) => void
  onClick?: () => void
  accessToken?: string | null
  isButton: boolean
  // 2: the PDF of the new design (backend report_v2); the old design keeps the old report
  version?: 1 | 2
}

const RETRIES = 40

export default function ReportDownloader({
  children, resultSet, onFailure, onClick, accessToken, isButton, version = 1
}: IProps) {
  const token = useSelector((state: IAlphaprocessState) => state.core.jwt)
  const { t, i18n } = useTranslation()
  // the PDF v2 is made in the language the app is used in (the profile language can differ)
  const language = useSelector((state: IAlphaprocessState) => state.core.currentLanguage) || i18n.language
  async function downloadReport(run = 1) {
    if (run > RETRIES) {
      toast(t('resultSet.couldNotGenerateReport'), { type: 'error' })
      return
    }

    let fetchUrl = `${process.env.REACT_APP_API_BASE_URL}/checklists/${
      resultSet.checklist_id}/result_sets/${resultSet.id}/${version === 2 ? 'report_v2' : 'report'}.pdf`

    const query = new URLSearchParams()
    if (accessToken) query.set('access_token', accessToken)
    if (version === 2 && language) query.set('lang', language.slice(0, 2))
    if (query.toString()) fetchUrl += `?${query.toString()}`

    try {
      const response = await fetch(fetchUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (response.status === 200) {
        const parsedBody = await response.json()
        if ('cordova' in window) {
          (window as any).cordova.InAppBrowser.open(parsedBody.url, '_system', 'location=yes')
        } else {
          let blob: Blob
          try {
            const pdfRequest = await fetch(parsedBody.url)
            if (!pdfRequest.ok) throw new Error(`report download ${pdfRequest.status}`)
            blob = await pdfRequest.blob()
          } catch (e) {
            // the storage refuses cross-origin downloads from other origins (S3 CORS allows only the
            // app's domain, e.g. not a local dev server): open the file itself, a plain link is fine
            const direct = document.createElement('a')
            direct.href = parsedBody.url
            direct.target = '_blank'
            direct.rel = 'noopener'
            document.body.appendChild(direct)
            direct.click()
            direct.parentNode.removeChild(direct)
            return
          }
          const url = (window as Window & typeof globalThis).URL.createObjectURL(new Blob([blob]))
          const link = document.createElement('a')
          link.href = url
          
          // Sanitize the title to prevent XSS attacks
          /* ✗ [Medium] Cross-site Scripting (XSS) Path: src/components/checklists/result/ReportDownloader.tsx, line 58 Info: Unsanitized input from data from a remote resource flows into appendChild, where it is used to dynamically construct the HTML page on client side. This may result in a DOM Based Cross-Site Scripting attack (DOMXSS). */
          const sanitizedTitle = DOMPurify.sanitize(resultSet.title);
          const pdfFilename = buildResultSetFilename(sanitizedTitle, resultSet.location, resultSet.closed_at || resultSet.due_date, 'pdf');
          link.setAttribute('download', pdfFilename);

          document.body.appendChild(link)
          link.click()
          link.parentNode.removeChild(link)
        }
      } else if (response.status === 420) {
        setTimeout(() => downloadReport(run + 1), 3000)
        if (run === 1) {
          toast(t('resultSet.generatingReport'), { type: 'info' })
        }
      } else if (response.status === 204) {
        alert(t('resultSet.noDataForReport'))
      } else {
        toast(t('resultSet.couldNotGenerateReport'), { type: 'error' })
      }
    } catch (error) {
      if (typeof onFailure === 'function') {
        onFailure(error as AxiosError)
      } else {
        toast(t('resultSet.couldNotGenerateReport'), { type: 'error' })
      }
    }
  }

  const onClickDownloadButton = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (onClick) {
      onClick()
    }
    return downloadReport()
  }
  return (
    <Link
      onClick={onClickDownloadButton}
      className={isButton ? 'report-downloader is-link button' : 'report-downloader is-link'}
      to="#"
    >
      { children }
    </Link>
  )
}
